// src/services/stockService.js
//
// Frontend API wrapper for the Stock / Inventory module.
//
// IMPORTANT ARCHITECTURE
// ----------------------
// Product catalog remains responsible for product master data.
// This service is responsible only for stock locations, balances and movements.
// It follows the same pattern as the other hb_visits frontend services: a thin
// wrapper around apiClient, local payload normalization and consistent error
// messages for the page layer.
//
// Do not put valuation logic here. Moving average cost, negative-stock checks
// and movement creation/correction belong to the backend stock domain services.

import apiClient from "../utils/apiClient";
import { idempotentPost } from "../utils/idempotentStockPosting";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

export const STOCK_MOVEMENT_TYPES = Object.freeze({
  RECEIPT: "receipt",
  ISSUE: "issue",
  SALE: "sale",
  TRANSFER: "transfer",
  CORRECTION: "correction",
  VALUATION_ADJUSTMENT: "valuation_adjustment",
  SERVICE_CONSUMPTION: "service_consumption",
  SERVICE_CONSUMPTION_REVERSAL: "service_consumption_reversal",
  SANITARY_CONSUMPTION: "sanitary_consumption",
  SANITARY_CONSUMPTION_REVERSAL: "sanitary_consumption_reversal",
  SANITARY_SOLUTION_PREPARATION: "sanitary_solution_preparation",
  RETURN: "return",
});

export const STOCK_BATCH_POLICIES = Object.freeze({
  NONE: "none",
  FEFO: "FEFO",
  MANUAL: "MANUAL",
});

/** Extract the most useful error message from the backend response shape. */
function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

/**
 * Re-throw API errors with endpoint context so the UI can show useful messages
 * while preserving status/code for future form-level handling.
 */
function throwApiError(context, err) {
  // AbortController cancellations are expected control flow when filters change,
  // tabs switch or the page unmounts. Preserve the original Axios cancellation
  // object so shared isRequestCanceled() can recognize it in the page layer.
  if (err?.__CANCEL__ === true || err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
    throw err;
  }

  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status || err?.status;
  e.outcomeUnknown = Boolean(err?.outcomeUnknown || err?.response?.data?.outcome_unknown);
  e.data = err?.response?.data || err?.data;
  e.code = err?.response?.data?.code || err?.code;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[StockService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function isBlank(v) {
  return v === undefined || v === null || `${v}`.trim() === "";
}

/** Guard against accidental calls with missing request bodies. */
function assertPayload(context, payload) {
  if (!payload || typeof payload !== "object") {
    const e = new Error(`${context}: payload object is required.`);
    e.code = "STOCK_PAYLOAD_MISSING";
    throw e;
  }
}

/**
 * Lightweight client-side required-field validation.
 *
 * The backend still validates everything authoritatively; this only prevents
 * obvious empty submissions and gives faster feedback to the user.
 */
function assertRequired(context, payload, keys = []) {
  assertPayload(context, payload);

  keys.forEach((key) => {
    if (isBlank(payload[key])) {
      const e = new Error(`${context}: ${key} is required.`);
      e.code = "STOCK_FIELD_MISSING";
      e.field = key;
      throw e;
    }
  });
}

function cleanOptionalText(v) {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s || null;
}

/**
 * Keep numeric form input compatible with backend decimal parsing.
 *
 * Users in PL/DE contexts often type comma decimals; backend accepts both after
 * this normalization.
 */
function cleanNumberLike(v) {
  if (v === undefined || v === null || `${v}`.trim() === "") return null;
  return String(v).replace(",", ".");
}


function normalizeMovementCorrectionPayload(payload = {}) {
  return {
    quantity: cleanNumberLike(payload.quantity),
    from_location_id: cleanOptionalText(payload.from_location_id),
    to_location_id: cleanOptionalText(payload.to_location_id),
    cost_entry_mode: cleanOptionalText(payload.cost_entry_mode),
    unit_cost_net: cleanNumberLike(payload.unit_cost_net),
    package_unit_cost_net: cleanNumberLike(payload.package_unit_cost_net),
    total_cost_net: cleanNumberLike(payload.total_cost_net),
    batch_allocations: Array.isArray(payload.batch_allocations)
      ? payload.batch_allocations.map((row) => ({
          id: cleanOptionalText(row.id),
          batch_id: cleanOptionalText(row.batch_id),
          quantity: cleanNumberLike(row.quantity),
        }))
      : undefined,
    reason: cleanOptionalText(payload.reason),
    comment: cleanOptionalText(payload.comment),
  };
}


function normalizeValueAdjustmentPayload(payload = {}) {
  return {
    product_id: cleanOptionalText(payload.product_id),
    batch_id: cleanOptionalText(payload.batch_id),
    business_date: cleanOptionalText(payload.business_date),
    input_mode: cleanOptionalText(payload.input_mode),
    target_value_net: cleanNumberLike(payload.target_value_net),
    target_average_cost_net: cleanNumberLike(payload.target_average_cost_net),
    value_delta_net: cleanNumberLike(payload.value_delta_net),
    expected_before: payload.expected_before || undefined,
    reason: cleanOptionalText(payload.reason),
    comment: cleanOptionalText(payload.comment),
  };
}

function normalizePagedResponse(data, fallbackPageSize = 50) {
  // Keep a compatibility path for a temporarily older backend during rolling
  // deployment. Once the paged backend is present, the object shape below is
  // used and total/page_count come from the server.
  if (Array.isArray(data)) {
    const total = data.length;
    return {
      items: data,
      pagination: {
        page: 1,
        page_size: fallbackPageSize,
        total,
        page_count: 1,
      },
    };
  }

  const items = Array.isArray(data?.items) ? data.items : [];
  const raw = data?.pagination || {};
  const pageSize = Math.max(Number(raw.page_size || fallbackPageSize) || fallbackPageSize, 1);
  const total = Math.max(Number(raw.total || 0) || 0, 0);
  const pageCount = Math.max(Number(raw.page_count || Math.ceil(total / pageSize) || 1) || 1, 1);
  const page = Math.min(Math.max(Number(raw.page || 1) || 1, 1), pageCount);

  return {
    items,
    pagination: {
      page,
      page_size: pageSize,
      total,
      page_count: pageCount,
    },
  };
}

/** Fetch active stock locations for filters and movement forms. */
export const fetchStockLocations = async (params = {}, options = {}) => {
  try {
    const res = await apiClient.get("/stock/locations", {
      withCredentials: true,
      params,
      signal: options.signal,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("fetchStockLocations", err);
  }
};


/**
 * Search products with stock context.
 *
 * This endpoint is used by the shared ProductPicker component. It replaces the
 * earlier Phase 1 pattern where the stock page loaded all products and rendered
 * them in a native dropdown. The picker needs product master data plus stock
 * context such as selected-location availability, global quantity and moving
 * average cost.
 */
export const searchStockProducts = async (params = {}) => {
  try {
    const res = await apiClient.get("/stock/products/search", {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("searchStockProducts", err);
  }
};

/**
 * Fetch current stock balances.
 *
 * Returned rows include product/location details and the aggregate product
 * average cost as a display field. Under batch valuation this aggregate is
 * derived from current LOT values. The frontend does not calculate stock value.
 */
export const fetchStockBalances = async (params = {}, options = {}) => {
  try {
    const res = await apiClient.get("/stock/balances", {
      withCredentials: true,
      params,
      signal: options.signal,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("fetchStockBalances", err);
  }
};

/**
 * Fetch the paged master product browser used by the warehouse workspace.
 *
 * This is intentionally separate from searchStockProducts(), whose array
 * response is kept stable for the shared ProductPicker component.
 */
export const fetchStockWorkspaceProducts = async (params = {}, options = {}) => {
  try {
    const res = await apiClient.get("/stock/products/workspace", {
      withCredentials: true,
      params,
      signal: options.signal,
    });

    return normalizePagedResponse(res?.data, Number(params.page_size || 25));
  } catch (err) {
    throwApiError("fetchStockWorkspaceProducts", err);
  }
};

/** Fetch batch / expiry master rows for product/lot lookup screens. */
export const fetchStockBatches = async (params = {}, options = {}) => {
  try {
    const res = await apiClient.get("/stock/batches", {
      withCredentials: true,
      params,
      signal: options.signal,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("fetchStockBatches", err);
  }
};

/** Fetch paged current quantity per batch and location. */
export const fetchStockBatchBalances = async (params = {}, options = {}) => {
  try {
    const res = await apiClient.get("/stock/batch-balances", {
      withCredentials: true,
      params,
      signal: options.signal,
    });

    return normalizePagedResponse(res?.data, Number(params.page_size || 50));
  } catch (err) {
    throwApiError("fetchStockBatchBalances", err);
  }
};

/** Fetch paged stock movements for the audit/history table. */
export const fetchStockMovements = async (params = {}, options = {}) => {
  try {
    const res = await apiClient.get("/stock/movements", {
      withCredentials: true,
      params,
      signal: options.signal,
    });

    return normalizePagedResponse(res?.data, Number(params.page_size || 50));
  } catch (err) {
    throwApiError("fetchStockMovements", err);
  }
};

/**
 * Post stock receipt.
 *
 * The required unit_cost_net is the actual purchase/net receipt cost that the
 * backend folds into the global moving average cost. Quantity can be provided
 * directly or calculated by backend from package_quantity and product content.
 */
export const receiveStock = async (payload) => {
  try {
    assertRequired("receiveStock", payload, ["product_id", "location_id"]);
    if (!payload?.quantity && !payload?.package_quantity) {
      throw new Error("receiveStock missing required quantity or package_quantity");
    }

    const costMode = cleanOptionalText(payload.cost_entry_mode || "stock_unit");
    if (costMode === "package" && isBlank(payload?.package_unit_cost_net)) {
      throw new Error("receiveStock missing required package_unit_cost_net");
    }
    if (costMode === "total" && isBlank(payload?.total_cost_net)) {
      throw new Error("receiveStock missing required total_cost_net");
    }
    if (!costMode || costMode === "stock_unit") {
      assertRequired("receiveStock", payload, ["unit_cost_net"]);
    }

    const res = await idempotentPost(apiClient,
      "/stock/receive",
      {
        product_id: payload.product_id,
        location_id: payload.location_id,
        business_date: cleanOptionalText(payload.business_date),
        quantity: cleanNumberLike(payload.quantity),
        package_quantity: cleanNumberLike(payload.package_quantity),
        cost_entry_mode: costMode,
        unit_cost_net: cleanNumberLike(payload.unit_cost_net),
        package_unit_cost_net: cleanNumberLike(payload.package_unit_cost_net),
        total_cost_net: cleanNumberLike(payload.total_cost_net),
        reference_type: cleanOptionalText(payload.reference_type || "manual"),
        reference_id: cleanOptionalText(payload.reference_id),
        reason: cleanOptionalText(payload.reason || "receipt"),
        comment: cleanOptionalText(payload.comment),
        batch_number: cleanOptionalText(payload.batch_number),
        expiry_date: cleanOptionalText(payload.expiry_date),
        supplier_name: cleanOptionalText(payload.supplier_name),
        receipt_document_no: cleanOptionalText(payload.receipt_document_no),
        received_at: cleanOptionalText(payload.received_at),
        batch_comment: cleanOptionalText(payload.batch_comment),
        receipt_batches: Array.isArray(payload.receipt_batches)
          ? payload.receipt_batches.map((row) => ({
              batch_number: cleanOptionalText(row?.batch_number),
              expiry_date: cleanOptionalText(row?.expiry_date),
              quantity: cleanNumberLike(row?.quantity),
              package_quantity: cleanNumberLike(row?.package_quantity),
              comment: cleanOptionalText(row?.comment),
            }))
          : [],
      },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("receiveStock", err);
  }
};

/**
 * Post manual stock issue.
 *
 * The frontend does not send a unit cost here. Backend values the issue using
 * the product's configured valuation policy (product moving average or concrete
 * batch/LOT moving averages).
 */
export const issueStock = async (payload) => {
  try {
    assertRequired("issueStock", payload, ["product_id", "location_id", "quantity"]);

    const res = await idempotentPost(apiClient,
      "/stock/issue",
      {
        product_id: payload.product_id,
        location_id: payload.location_id,
        business_date: cleanOptionalText(payload.business_date),
        quantity: cleanNumberLike(payload.quantity),
        reference_type: cleanOptionalText(payload.reference_type || "manual"),
        reference_id: cleanOptionalText(payload.reference_id),
        reason: cleanOptionalText(payload.reason || "issue"),
        comment: cleanOptionalText(payload.comment),
        batch_policy: cleanOptionalText(payload.batch_policy),
        batch_id: cleanOptionalText(payload.batch_id),
      },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("issueStock", err);
  }
};

/**
 * Transfer stock between two locations.
 *
 * Transfer is quantity/location-only from a business perspective. Backend keeps
 * global product quantity/value/average unchanged.
 */
export const transferStock = async (payload) => {
  try {
    assertRequired("transferStock", payload, ["product_id", "from_location_id", "to_location_id", "quantity"]);

    const res = await idempotentPost(apiClient,
      "/stock/transfer",
      {
        product_id: payload.product_id,
        from_location_id: payload.from_location_id,
        to_location_id: payload.to_location_id,
        business_date: cleanOptionalText(payload.business_date),
        quantity: cleanNumberLike(payload.quantity),
        reference_type: cleanOptionalText(payload.reference_type || "manual"),
        reference_id: cleanOptionalText(payload.reference_id),
        reason: cleanOptionalText(payload.reason || "transfer"),
        comment: cleanOptionalText(payload.comment),
        batch_policy: cleanOptionalText(payload.batch_policy),
        batch_id: cleanOptionalText(payload.batch_id),
      },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("transferStock", err);
  }
};

/**
 * Post a signed stock correction.
 *
 * Corrections require reason/comment because they are manual audit-sensitive
 * adjustments. Positive corrections may include a cost; negative corrections are
 * valued by the backend at the global product average.
 */
export const correctStock = async (payload) => {
  try {
    assertRequired("correctStock", payload, ["product_id", "location_id", "quantity_delta", "reason", "comment"]);

    const res = await idempotentPost(apiClient,
      "/stock/correction",
      {
        product_id: payload.product_id,
        location_id: payload.location_id,
        business_date: cleanOptionalText(payload.business_date),
        quantity_delta: cleanNumberLike(payload.quantity_delta),
        unit_cost_net: cleanNumberLike(payload.unit_cost_net),
        reference_type: cleanOptionalText(payload.reference_type || "correction"),
        reference_id: cleanOptionalText(payload.reference_id),
        reason: cleanOptionalText(payload.reason),
        comment: cleanOptionalText(payload.comment),
        batch_policy: cleanOptionalText(payload.batch_policy),
        batch_id: cleanOptionalText(payload.batch_id),
        batch_number: cleanOptionalText(payload.batch_number),
        expiry_date: cleanOptionalText(payload.expiry_date),
        supplier_name: cleanOptionalText(payload.supplier_name),
        receipt_document_no: cleanOptionalText(payload.receipt_document_no),
        received_at: cleanOptionalText(payload.received_at),
        batch_comment: cleanOptionalText(payload.batch_comment),
      },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("correctStock", err);
  }
};



/** Preview a current value-only stock adjustment (WOM). */
export const previewStockValueAdjustment = async (payload) => {
  try {
    assertRequired("previewStockValueAdjustment", payload, ["product_id", "input_mode", "reason", "comment"]);
    const res = await apiClient.post(
      "/stock/value-adjustments/preview",
      normalizeValueAdjustmentPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError("previewStockValueAdjustment", err);
  }
};

/** Post the exact current WOM after a fresh preview using the stock retry journal. */
export const applyStockValueAdjustment = async (payload) => {
  try {
    assertRequired("applyStockValueAdjustment", payload, ["product_id", "input_mode", "reason", "comment"]);
    if (!payload.expected_before) {
      const e = new Error("applyStockValueAdjustment: preview is required.");
      e.code = "STOCK_VALUE_ADJUSTMENT_PREVIEW_REQUIRED";
      throw e;
    }
    const res = await idempotentPost(
      apiClient,
      "/stock/value-adjustments",
      normalizeValueAdjustmentPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError("applyStockValueAdjustment", err);
  }
};

/** Preview an amendment of one recorded manual stock movement. */
export const previewStockMovementCorrection = async (movementId, payload) => {
  try {
    assertPayload("previewStockMovementCorrection", payload);
    assertRequired("previewStockMovementCorrection", payload, ["quantity", "reason", "comment"]);
    const res = await apiClient.post(
      `/stock/movements/${movementId}/correction-preview`,
      normalizeMovementCorrectionPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError("previewStockMovementCorrection", err);
  }
};

/** Apply the exact correction command with the normal stock retry journal. */
export const applyStockMovementCorrection = async (movementId, payload) => {
  try {
    assertPayload("applyStockMovementCorrection", payload);
    assertRequired("applyStockMovementCorrection", payload, ["quantity", "reason", "comment"]);
    const res = await idempotentPost(
      apiClient,
      `/stock/movements/${movementId}/correction`,
      normalizeMovementCorrectionPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError("applyStockMovementCorrection", err);
  }
};

export default {
  STOCK_MOVEMENT_TYPES,
  STOCK_BATCH_POLICIES,
  fetchStockLocations,
  searchStockProducts,
  fetchStockBalances,
  fetchStockWorkspaceProducts,
  fetchStockBatches,
  fetchStockBatchBalances,
  fetchStockMovements,
  receiveStock,
  issueStock,
  transferStock,
  correctStock,
  previewStockValueAdjustment,
  applyStockValueAdjustment,
  previewStockMovementCorrection,
  applyStockMovementCorrection,
};


// A single HTTP response contains a consistent product/LOT/ledger snapshot.
export const fetchStockProductContext = async (id, params = {}, config = {}) => {
  try { const res = await apiClient.get(`/stock/products/${id}/context`, { ...config, params, withCredentials: true }); return res.data; }
  catch (error) { throwApiError("fetchStockProductContext", error); }
};
export const fetchStockMovementDetail = async (id, config = {}) => {
  try { const res = await apiClient.get(`/stock/movements/${id}`, { ...config, withCredentials: true }); return res.data; }
  catch (error) { throwApiError("fetchStockMovementDetail", error); }
};

